import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Box, Button, Checkbox, Dialog,
  DialogActions,
  DialogTitle, Divider, FormControl,
  FormControlLabel, IconButton,
  InputAdornment, InputLabel, OutlinedInput, Paper,
  Theme,
  ThemeOptions,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Breakpoint, Stack } from '@mui/system';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import { DirectionDisplaySettingEntity } from 'src/entities/DirectionDisplaySetting.entity';

import { buildDirectionPrintPath } from './directionPrintPath';

// 「納品のみ表示」「省略表示」のチェック状態を localStorage に保存し、次回も再現する。
const PRINT_TOGGLES_STORAGE_KEY = 'operationDirectionPrintToggles';
type PrintToggles = { displayOnlyUnloading: boolean; truncate: boolean };
const readPrintToggles = (): PrintToggles => {
  try {
    const raw = localStorage.getItem(PRINT_TOGGLES_STORAGE_KEY);
    if (!raw) return { displayOnlyUnloading: false, truncate: false };
    const parsed = JSON.parse(raw) as Partial<PrintToggles>;
    return {
      displayOnlyUnloading: parsed.displayOnlyUnloading === true,
      truncate: parsed.truncate === true,
    };
  } catch {
    return { displayOnlyUnloading: false, truncate: false };
  }
};
const writePrintToggles = (value: PrintToggles): void => {
  try {
    localStorage.setItem(PRINT_TOGGLES_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage 不可環境は無視（既定値で動作）。
  }
};

const Card: FC<{
  setting: DirectionDisplaySettingEntity
  label: string
  index: number
  onDelete: () => void
}> = memo((
  {
    setting,
    label,
    index,
    onDelete,
  }
) => {
  const {
    setActivatorNodeRef,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: setting.id });

  return (
    <Paper
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      variant="outlined"
      sx={{ px: 1, py: 0.5 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
      >
        {/* ドラッグハンドル（左）。attributes/listeners はフォーカスを受けるハンドルに付与する（a11y）。 */}
        <Box
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            touchAction: 'none',
            color: 'text.secondary',
          }}
        >
          <DragIndicatorRoundedIcon fontSize="small" />
        </Box>
        <Typography sx={{ minWidth: 20, textAlign: 'right', color: 'text.secondary' }}>
          {index}
        </Typography>
        <Typography sx={{ flexGrow: 1 }}>{label}</Typography>
        {/* 削除ボタン（右） */}
        <IconButton size="small" onClick={onDelete} aria-label="削除">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
});

type Props = {
  open: boolean;
  onClose: () => void;
  width: Breakpoint;
  navigateToPath: string;
}

const DirectionSettingDialog: FC<Props> = memo((
  {
    open,
    onClose,
    width,
    navigateToPath,
  }
) => {
  const theme: Theme & ThemeOptions = useTheme();
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();

  const searchBoxHeight = 55;
  const contentHeight = '70vh';
  const maximumNumberOfDisplaySettings = 15;

  const selectableDisplayDirectionConditionSettings = licenseContext?.selectableDisplayDirectionConditionSettings || [];
  const rawDefaultSettings = licenseContext?.displayDirectionConditions?.find((it) => it.name === 'default')?.settings;
  let settings: DirectionDisplaySettingEntity[] = Array.isArray(rawDefaultSettings) ? rawDefaultSettings : [];
  if (selectableDisplayDirectionConditionSettings.length > 0 && settings.length > 0) {
    const ids = selectableDisplayDirectionConditionSettings.map((it) => it.id);
    settings = settings.filter((it) => ids.includes(it.id));
  }
  // 表示ラベル（案A: 保存設定の title が旧名でも、選択マスタの title=新ラベルで表示する）。
  const labelOf = (id: string): string => (
    selectableDisplayDirectionConditionSettings.find((it) => it.id === id)?.title ?? id
  );

  const canNavigate = !!settings.length;
  const numberOfDisplaySettings = settings.length;
  const canAddDisplaySettings = numberOfDisplaySettings < maximumNumberOfDisplaySettings;

  const [searchKw, setSearchKw] = useState<string | null>(null);
  // 「納品のみ表示」「省略表示」は localStorage に保存して次回も再現する。
  const [displayOnlyUnloading, setDisplayOnlyUnloading] = useState<boolean>(() => readPrintToggles().displayOnlyUnloading);
  const [truncate, setTruncate] = useState<boolean>(() => readPrintToggles().truncate);

  const navigateButtonOnClick = () => {
    window.open(buildDirectionPrintPath(navigateToPath, { displayOnlyUnloading, truncate }), '_blank');
  };

  const displayOnlyUnloadingOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.checked;
    setDisplayOnlyUnloading(next);
    writePrintToggles({ displayOnlyUnloading: next, truncate });
  };

  const truncateOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.checked;
    setTruncate(next);
    writePrintToggles({ displayOnlyUnloading, truncate: next });
  };

  const displayDirectionConditionCardOnClick = (setting: DirectionDisplaySettingEntity) => {
    if (!licenseContext?.displayDirectionConditions) return;

    const settingIsExists = settings.some((it) => it.id === setting.id);

    if (settingIsExists) {
      const value = {
        name: 'default',
        settings: settings.filter((it) => it.id !== setting.id)
      };

      licenseContext.updateDisplayDirectionCondition(value);
    } else {
      if (!canAddDisplaySettings) {
        enqueueSnackbar(`表示項目は${maximumNumberOfDisplaySettings}個までです。`, {
          variant: 'warning',
        });

        return;
      }

      const value = {
        name: 'default',
        settings: [
          setting,
          ...settings,
        ]
      };

      licenseContext.updateDisplayDirectionCondition(value);
    }
  };

  const updateSearchKw = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      setSearchKw(null);
      return;
    }

    setSearchKw(event.target.value);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
      }}
      fullWidth
      maxWidth={width}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.colors.alpha.trueWhite[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        運行指示書 - 表示項目設定
        <IconButton
          onClick={() => onClose()}
          aria-label="閉じる"
          sx={{ color: 'inherit' }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <Stack
        direction="row"
        sx={{
          height: `calc(${contentHeight} - ${searchBoxHeight}px)`,
        }}
        justifyContent="space-between"
      >
        <Stack
          flexGrow={1}
        >
          <Typography sx={{ px: 1, pt: 1 }}>
            選択可能な項目（クリックで追加／削除）
          </Typography>
          <Stack
            p={1}
            sx={{
              height: `${searchBoxHeight}px`,
            }}
          >
            <FormControl variant="outlined">
              <InputLabel
                size="small"
                htmlFor="direction-display-settings-search"
              >
                検索
              </InputLabel>
              <OutlinedInput
                id="direction-display-settings-search"
                onChange={updateSearchKw}
                size="small"
                value={searchKw || ''}
                endAdornment={(
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchKw(null);
                      }}
                      edge="end"
                      disabled={!searchKw}
                    >
                      <HighlightOffRoundedIcon color={searchKw ? 'error' : 'secondary'} />
                    </IconButton>
                  </InputAdornment>
                )}
                label="検索"
              />
            </FormControl>
          </Stack>
          <Stack
            divider={<Divider />}
            sx={{
              height: contentHeight,
              overflowY: 'scroll'
            }}
          >
            {
              selectableDisplayDirectionConditionSettings
                .filter((it) => {
                  if (!searchKw) return true;

                  return it.title.includes(searchKw);
                })
                .map(((selectableSetting) => (
                  <Stack
                    key={[
                      'directionSettingDialog',
                      'selectableDisplayDirectionConditionSettings',
                      selectableSetting.id
                    ].join('-')}
                    direction="row"
                    alignItems="center"
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      displayDirectionConditionCardOnClick(selectableSetting);
                    }}
                  >
                    <Checkbox
                      checked={
                        settings.some((item) => item.id === selectableSetting.id)
                      }
                      tabIndex={-1}
                      disableRipple
                    />
                    {selectableSetting.title}
                  </Stack>
                )))
            }
          </Stack>
        </Stack>
        <Stack
          flexGrow={1}
        >
          <Stack
            justifyContent="center"
            alignItems="flex-start"
            sx={{
              height: `${searchBoxHeight}px`,
              p: 1
            }}
          >
            <Typography>
              {
                [
                  '表示順（ドラッグ＆ドロップで並び替え）',
                  `${numberOfDisplaySettings} / ${maximumNumberOfDisplaySettings} 項目`,
                ].join(' ')
              }
            </Typography>
          </Stack>
          <Stack
            spacing={1}
            sx={{
              height: `calc(${contentHeight} - ${searchBoxHeight}px)`,
              p: 1,
              overflowY: 'scroll',
              overflowX: 'hidden'
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (over == null || active.id === over.id) {
                  return;
                }

                const oldIndex = settings.findIndex((item) => item.id === active.id);
                const newIndex = settings.findIndex((item) => item.id === over.id);
                const newSettings = arrayMove(settings, oldIndex, newIndex);

                const value = {
                  name: 'default',
                  settings: newSettings
                };

                licenseContext.updateDisplayDirectionCondition(value);
              }}
              modifiers={[
                restrictToVerticalAxis,
                restrictToParentElement
              ]}
            >
              <SortableContext
                items={
                  settings
                }
              >
                {
                  settings.map((setting, index) => (
                    <Card
                      key={
                        [
                          'directionSettingDialog',
                          'displayDirectionConditionSettings',
                          setting.id,
                        ].join('-')
                      }
                      setting={setting}
                      label={labelOf(setting.id)}
                      index={index + 1}
                      onDelete={() => displayDirectionConditionCardOnClick(setting)}
                    />
                  ))
                }
              </SortableContext>
            </DndContext>
          </Stack>
        </Stack>
      </Stack>
      <DialogActions>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={2}
          sx={{
            width: '100%',
            // 下部ボタン・トグル文言を改行させない（横幅が足りない場合も1行で表示）。
            '& .MuiButton-root': { whiteSpace: 'nowrap', flexShrink: 0 },
            '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={displayOnlyUnloading}
                  onChange={displayOnlyUnloadingOnChange}
                />
              )}
              label="納品のみ表示する"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={truncate}
                  onChange={truncateOnChange}
                />
              )}
              label="省略表示"
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
          >
            <Button
              startIcon={<SaveRoundedIcon />}
              onClick={() => {
                licenseContext.saveDisplayDirectionConditions();
              }}
              disabled={settings.length === 0}
            >
              初期状態として保存
            </Button>
            <Button
              startIcon={<RotateLeftRoundedIcon />}
              onClick={() => {
                licenseContext.resetDisplayDirectionCondition();
              }}
            >
              初期状態にリセット
            </Button>
            <Button
              variant="contained"
              startIcon={<ExploreRoundedIcon />}
              disabled={!canNavigate}
              onClick={navigateButtonOnClick}
            >
              運行指示書を表示する
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
});

export default DirectionSettingDialog;
